import { Badge, Button } from '@/components/ui'
import { TabBar, tabId, tabPanelId } from '@/components/TabBar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useWorkflowTriggerSchema } from '@/hooks'
import { formatDurationMs, formatTimestampShort } from '@/lib/timeFormatters'
import type { ChainRunLink } from '@/types'
import { triggerOnLabel } from '@/types'
import { CircleAlert, ExternalLink, Link2, Loader2, MessageSquare, X } from 'lucide-react'
import { useState } from 'react'
import { WorkflowStatusBadge } from '../../workflows/shared'
import { describeInputSource, resolveInputDisplayValue } from './chainLinkInputs'

type Tab = 'inputs' | 'run'

interface Props {
  link: ChainRunLink
  projectId: string
  isWaiting: boolean
  onClose: () => void
  onOpenRun: (workflowRunId: string) => void
  onOpenChat: (chatId: string) => void
}

const tabs: { key: Tab; label: string }[] = [
  { key: 'inputs', label: 'Inputs' },
  { key: 'run', label: 'Run' },
]

const LINK_TABS = 'chain-link-tabs'

function InputSourceCell({ link, name }: { link: ChainRunLink; name: string }) {
  const source = describeInputSource(link, name)

  if (source.kind === 'mapping') {
    return (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px] break-all">{source.expression}</code>
    )
  }

  if (source.kind === 'supplied') {
    return <span className="text-xs text-muted-foreground">supplied at start</span>
  }

  if (source.kind === 'missing') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-danger-foreground">
        <CircleAlert className="h-3 w-3" /> missing
      </span>
    )
  }

  return <span className="text-xs text-muted-foreground">workflow default</span>
}

export function ChainLinkDetailPanel({ link, projectId, isWaiting, onClose, onOpenRun, onOpenChat }: Props) {
  const [tab, setTab] = useState<Tab>('inputs')
  const { data: schema, isLoading, isError } = useWorkflowTriggerSchema(projectId, link.workflowName)

  const fields = (schema?.fields ?? []).filter(f => !f.hidden)
  const run = link.run
  const badgeStatus = link.status === 'Triggered' ? run?.status ?? 'Triggered' : link.status
  const workflowRunId = run?.workflowRunId ?? link.workflowRunId

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden">
      <div className="p-4 border-b shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Link2 className="h-5 w-5 text-muted-foreground" />
            <span className="font-semibold">{link.linkName}</span>
            <WorkflowStatusBadge status={badgeStatus} />
            <Badge variant="secondary" className="text-xs">{link.workflowName}</Badge>
            <Badge variant="outline" className="text-xs">{triggerOnLabel(link.triggerOn)}</Badge>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose} aria-label="Close link details">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 flex flex-col gap-3">
        <TabBar tabs={tabs} activeTab={tab} onTabChange={setTab} idPrefix={LINK_TABS} />

        <div
          id={tabPanelId(LINK_TABS)}
          role="tabpanel"
          aria-labelledby={tabId(LINK_TABS, tab)}
          className="flex-1 min-h-0 rounded-lg border overflow-y-auto bg-card p-4"
        >
          {tab === 'inputs' && (
            <div data-testid="chain-link-inputs">
              {isLoading ? (
                <div className="flex items-center gap-2" role="status" aria-live="polite">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading inputs…</span>
                </div>
              ) : isError ? (
                <p className="text-sm text-muted-foreground">The inputs of this workflow could not be loaded.</p>
              ) : fields.length === 0 ? (
                <p className="text-sm text-muted-foreground">This workflow takes no inputs.</p>
              ) : (
                <Table aria-label={`Inputs of link ${link.linkName}`}>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-[35%]">Input</TableHead>
                      <TableHead className="w-[35%]">Source</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map(field => (
                      <TableRow key={field.name}>
                        <TableCell className="text-xs font-medium align-top">{field.label ?? field.name}</TableCell>
                        <TableCell className="align-top"><InputSourceCell link={link} name={field.name} /></TableCell>
                        <TableCell className="text-xs align-top break-all">{resolveInputDisplayValue(link, field)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {isWaiting && (
                <p className="mt-3 text-xs text-muted-foreground">
                  No run yet — the link starts as soon as the missing input is supplied.
                </p>
              )}
            </div>
          )}

          {tab === 'run' && (
            <div data-testid="chain-link-run">
              {run ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">Status</span>
                      <WorkflowStatusBadge status={run.status} />
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Duration</span>
                      <span className="font-medium">{formatDurationMs(run.durationMs)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Started</span>
                      <span className="font-medium">{formatTimestampShort(run.startedAt)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Completed</span>
                      <span className="font-medium">{formatTimestampShort(run.completedAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-toolbar border-border-accent"
                      onClick={() => onOpenRun(run.workflowRunId)}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" /> Open run
                    </Button>
                    {run.chatId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-toolbar border-border-accent"
                        onClick={() => onOpenChat(run.chatId!)}
                      >
                        <MessageSquare className="h-3 w-3 mr-1" /> Open chat
                      </Button>
                    )}
                  </div>
                </div>
              ) : workflowRunId ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">Run {workflowRunId.slice(0, 8)}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-toolbar border-border-accent"
                    onClick={() => onOpenRun(workflowRunId)}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" /> Open run
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">This link has not run yet.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
